import { useIsMobile } from '@side/pantry';
import { useAudit, useCore, usePayment } from '$api-clients/hooks';
import {
  buildCommissionFormUrl,
  getEnvelopeRequestQueryParams,
  LISTING_AGREEMENTS_NEEDS_ATTENTION_FILTERS,
} from './helpers';

export const useNeedsAttention = () => {
  const isMobile = useIsMobile();

  const { data: buyerAgreementsData, isLoading: buyerAgreementsLoading } =
    useCore('/interactions', {
      params: {
        query: {
          type: 'clientGroup',
          sort: 'endDate:asc',
          facets: 'isArchived:false',
          needsAttention: true,
          offset: 0,
          limit: 1,
        },
      },
    });

  const { data: listingAgreementsData, isLoading: listingAgreementsLoading } =
    useCore('/listings', {
      params: {
        query: {
          sort: 'listingEndDate:asc',
          facets: LISTING_AGREEMENTS_NEEDS_ATTENTION_FILTERS,
          needsAttention: true,
          offset: 0,
          limit: 1,
        },
      },
    });

  const {
    interactionStatusFacet,
    envelopeStatusFacet,
    envelopeSentAtFacet,
    envelopeReminderSentAtFacet,
  } = getEnvelopeRequestQueryParams();

  const { data: envelopesData, isLoading: envelopesLoading } = useCore(
    '/interactions',
    {
      params: {
        query: {
          sort: 'sentAt:asc',
          facets: `${interactionStatusFacet},${envelopeStatusFacet},${envelopeSentAtFacet},${envelopeReminderSentAtFacet}`,
          offset: 0,
          limit: 1,
        },
      },
    },
  );

  const { data: envelopesCountData, isLoading: envelopesCountLoading } =
    useCore('/interactions/envelopes', {
      params: {
        query: {
          sort: 'sentAt:asc',
          facets: interactionStatusFacet,
          needsAttention: true,
        },
      },
    });

  const { data: complianceReviewData, isLoading: complianceReviewLoading } =
    useAudit(
      '/audits' as '/audits/',
      isMobile
        ? null
        : {
            params: {
              query: {
                limit: 10,
                needsAttention: true,
              },
            },
          },
    );

  const { data: commissionFormData, isLoading: commissionFormLoading } =
    usePayment('/commissions' as '/commissions/', {
      params: {
        query: {
          limit: 10,
          filters: ['paymentStatusGroup:NEEDS_ATTENTION'],
        },
      },
    });

  // GET /commissions intentionally returns commissions the caller can't open, so
  // the permission gate has to happen here or view-only delegates get a 403 link
  const commissions = (commissionFormData?.results ?? [])
    .filter(
      (c) =>
        !!c.legacyExternalId &&
        c.me?.additionalPermissions?.includes('CAN_EDIT_COMMISSION_FORM'),
    )
    .map((c) => ({
      ...c,
      id: c.legacyExternalId,
    }));

  const { data: paymentInteractionData, isLoading: paymentInteractionLoading } =
    usePayment(
      '/transactions/{transactionId}',
      commissions.length > 1 || !commissions[0]?.id
        ? null
        : { params: { path: { transactionId: commissions[0].id } } },
    );

  // interactionType only builds the typed commission-form deep-link below, so a
  // missing value (a 4xx when the caller lacks core access) is non-blocking.
  const { data: interactionData, isLoading: interactionLoading } = useCore(
    '/interactions/{interactionId}',
    commissions.length > 1 || !commissions[0]?.interactionId
      ? null
      : {
          params: {
            path: { interactionId: commissions[0].interactionId },
          },
        },
  );

  const singleCommissionFormUrl = buildCommissionFormUrl(
    paymentInteractionData,
    interactionData?.type,
  );

  return {
    commissionForm: commissionFormData
      ? {
          interactionCount: commissions.length,
          commissions,
          singleCommissionFormUrl,
        }
      : undefined,
    complianceReview: complianceReviewData
      ? {
          audits: complianceReviewData.results,
          interactionCount: complianceReviewData.count,
        }
      : undefined,
    envelopes:
      envelopesData && envelopesCountData
        ? {
            envelopeCount: envelopesCountData.count,
            interactionCount: envelopesData.count,
            interactions: envelopesData.results.map(
              (result) => result.interaction,
            ),
          }
        : undefined,
    listingAgreements: listingAgreementsData
      ? {
          interactions: listingAgreementsData.results,
          interactionCount: listingAgreementsData.count,
        }
      : undefined,
    buyerAgreements: buyerAgreementsData
      ? {
          interactions: buyerAgreementsData.results.map(
            (result) => result.interaction,
          ),
          interactionCount: buyerAgreementsData.count,
        }
      : undefined,
    loading:
      paymentInteractionLoading ||
      interactionLoading ||
      commissionFormLoading ||
      buyerAgreementsLoading ||
      envelopesLoading ||
      envelopesCountLoading ||
      complianceReviewLoading ||
      listingAgreementsLoading,
  };
};
